import React, { useState, useCallback, useMemo } from 'react';
import {
  Archive,
  Clock,
  Database,
  Download,
  UploadCloud,
  Settings,
  Zap,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';

// --- Utility Functions & Mock Data ---

// Mocking a professional logging/notification system (no 'alert()')
const useNotification = () => {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);

  const notify = (msg) => {
    setMessage(msg);
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  };
  
  return { notify, message, visible };
};

const MOCK_BACKUP_HISTORY = [
  { id: 301, date: '2025-11-17 02:00:00', size: '4.2 GB', status: 'Success', type: 'Scheduled', details: 'Full Database & Assets' },
  { id: 302, date: '2025-11-10 02:00:00', size: '4.1 GB', status: 'Success', type: 'Scheduled', details: 'Full Database & Assets' },
  { id: 303, date: '2025-11-05 14:30:00', size: '1.5 GB', status: 'Success', type: 'Manual', details: 'HR Data Only' },
  { id: 304, date: '2025-11-03 02:00:00', size: '4.0 GB', status: 'Failed', type: 'Scheduled', details: 'Full Database & Assets' },
];

const MOCK_INITIAL_CONFIG = {
  schedule: 'Weekly', // Daily, Weekly, Monthly
  time: '02:00',
  scope: 'All', // All, HR, CRM
  retentionDays: 90,
};

// --- Reusable UI Components ---

const Panel = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white p-4 sm:p-5 rounded-xl shadow-lg border border-gray-100 transition-shadow hover:shadow-xl ${className}`}>
    <h2 className="text-sm font-semibold text-gray-700 flex items-center mb-4 pb-2 border-b border-gray-100">
      <Icon className="w-4 h-4 mr-2 text-green-600" />
      {title}
    </h2>
    <div className="text-xs text-gray-600 space-y-3">
      {children}
    </div>
  </div>
);

const ButtonPrimary = ({ onClick, children, className = '', disabled = false, icon: Icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-2 py-1 text-xs font-medium rounded-lg shadow-md transition-all duration-200 flex items-center justify-center
                focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 
                bg-green-600 text-white hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 disabled:text-gray-500 ${className}`}
  >
    {Icon && <Icon className="w-3 h-3 mr-1" />}
    {children}
  </button>
);

const ButtonSecondary = ({ onClick, children, className = '', icon: Icon }) => (
  <button
    onClick={onClick}
    className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors duration-200 border border-gray-300
                text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 ${className}`}
  >
    {children}
  </button>
);

const Badge = ({ children, type = 'info' }) => {
  let color = 'bg-gray-100 text-gray-600';
  if (type === 'success') color = 'bg-green-100 text-green-700';
  if (type === 'danger') color = 'bg-red-100 text-red-700';
  if (type === 'warning') color = 'bg-yellow-100 text-yellow-700';

  return (
    <span className={`px-2 py-0.5 inline-flex text-[10px] leading-4 font-semibold rounded-full ${color}`}>
      {children}
    </span>
  );
};

// --- Feature Components ---

const BackupConfiguration = ({ config, onSave, notify }) => {
  const [localConfig, setLocalConfig] = useState(config);

  const handleChange = (field, value) => {
    setLocalConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (localConfig.retentionDays < 30) {
        return notify('Retention must be at least 30 days.', 'danger');
    }
    onSave(localConfig);
    notify('Backup schedule configuration updated successfully.', 'success');
  };

  const scopeOptions = [
    { value: 'All', label: 'All Data & Assets (Recommended)' },
    { value: 'HR', label: 'Only HR Records (Personnel, Payroll, Leave)' },
    { value: 'CRM', label: 'Only CRM Data (Leads, Sales, Support)' },
  ];
  
  const scheduleOptions = ['Daily', 'Weekly', 'Monthly'];

  return (
    <Panel title="Automated Backup Configuration" icon={Settings}>
      <p className='text-[11px] text-gray-500'>Define the frequency, scope, and retention policy for automatic system backups.</p>

      {/* Schedule Dropdown */}
      <div className="flex justify-between items-center py-1">
        <label htmlFor="schedule" className="font-medium text-sm">Backup Frequency</label>
        <select
          id="schedule"
          value={localConfig.schedule}
          onChange={(e) => handleChange('schedule', e.target.value)}
          className="w-40 px-2 py-1 border border-gray-300 rounded-lg text-xs appearance-none bg-white"
        >
          {scheduleOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>

      {/* Time Input */}
      <div className="flex justify-between items-center py-1">
        <label htmlFor="time" className="font-medium text-sm">Preferred Time (UTC)</label>
        <input
          id="time"
          type="time"
          value={localConfig.time}
          onChange={(e) => handleChange('time', e.target.value)}
          className="w-40 px-2 py-1 border border-gray-300 rounded-lg text-xs"
        />
      </div>
      
      {/* Data Scope */}
      <div className="flex justify-between items-center py-1">
        <label htmlFor="scope" className="font-medium text-sm">Data Scope</label>
        <select
          id="scope"
          value={localConfig.scope}
          onChange={(e) => handleChange('scope', e.target.value)}
          className="w-40 px-2 py-1 border border-gray-300 rounded-lg text-xs appearance-none bg-white"
        >
          {scopeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>

      {/* Retention Days */}
      <div className="flex justify-between items-center py-1 border-t border-gray-100 pt-3">
        <label htmlFor="retention" className="font-medium text-sm">Retention Period (Days)</label>
        <input
          id="retention"
          type="number"
          min="30"
          value={localConfig.retentionDays}
          onChange={(e) => handleChange('retentionDays', parseInt(e.target.value) || 0)}
          className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs text-right"
        />
      </div>

      <ButtonPrimary onClick={handleSave} className="w-full mt-4" icon={CheckCircle}>
        Save Configuration
      </ButtonPrimary>
    </Panel>
  );
};

const ManualBackup = ({ notify, isProcessing, setIsProcessing }) => {
  
  const handleManualBackup = () => {
    setIsProcessing(true);
    notify('Manual backup started. This may take a few minutes...', 'warning');
    
    // Mock the long process
    setTimeout(() => {
      setIsProcessing(false);
      notify('Manual backup successfully completed (4.5 GB).', 'success');
      // In a real app, this would trigger a data refresh to update the history table
    }, 4000); 
  };

  return (
    <Panel title="Trigger Manual Backup" icon={Zap} className="h-full">
      <p className='text-[11px] text-gray-500 mb-4'>Instantly create a snapshot of the current system data outside of the scheduled window.</p>
      
      <div className="flex items-center space-x-3 bg-indigo-50 p-3 rounded-lg border border-indigo-200">
        <Database className="w-5 h-5 text-indigo-600 flex-shrink-0" />
        <p className="text-xs text-indigo-700">
            Current system data size: **~4.5 GB**
        </p>
      </div>

      <ButtonPrimary 
        onClick={handleManualBackup} 
        disabled={isProcessing}
        className="w-full mt-4" 
        icon={isProcessing ? RotateCcw : UploadCloud}
      >
        {isProcessing ? 'Backup in Progress (40%)...' : 'Start Full System Backup Now'}
      </ButtonPrimary>
      
      {isProcessing && (
        <div className='mt-2 text-[10px] text-center text-gray-500 animate-pulse'>
            Do not navigate away until process finishes.
        </div>
      )}
    </Panel>
  );
};

const BackupHistory = ({ history, notify }) => {
  
  const handleAction = (action, backup) => {
    if (action === 'download') {
      notify(`Initiating download for backup ${backup.id} (${backup.size}).`);
    } else if (action === 'restore') {
      // Prompt a warning before a destructive action (using notification style, not alert)
      notify(`WARNING: Restoration of backup ${backup.id} is simulated. This would typically require multi-step confirmation.`, 'danger');
    }
  };

  return (
    <Panel title="Backup History Log" icon={Clock} className="lg:col-span-2">
      <p className='text-[11px] text-gray-500'>A chronological record of all past backup operations, status, and size information.</p>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Date/Time', 'Size', 'Type', 'Details', 'Status', 'Actions'].map((header) => (
                <th key={header} className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {history.map((backup) => (
              <tr key={backup.id} className={backup.status === 'Failed' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{backup.date}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{backup.size}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{backup.type}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{backup.details}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge type={backup.status === 'Success' ? 'success' : 'danger'}>
                    {backup.status === 'Failed' ? (<AlertTriangle className='w-3 h-3 mr-1 inline' />) : (<CheckCircle className='w-3 h-3 mr-1 inline' />)}
                    {backup.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 whitespace-nowrap space-x-2">
                  <ButtonSecondary onClick={() => handleAction('download', backup)} className="text-blue-600 hover:text-blue-800 border-blue-200">
                    <Download className="w-3 h-3 mr-1" /> Download
                  </ButtonSecondary>
                  {backup.status === 'Success' && (
                    <ButtonSecondary onClick={() => handleAction('restore', backup)} className="text-red-600 hover:text-red-800 border-red-200">
                       Restore
                    </ButtonSecondary>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

// --- Main Component ---

export default function DataBackupRestore() {
  const [backupConfig, setBackupConfig] = useState(MOCK_INITIAL_CONFIG);
  const [backupHistory, setBackupHistory] = useState(MOCK_BACKUP_HISTORY);
  const [isProcessing, setIsProcessing] = useState(false);
  const { notify, message, visible } = useNotification();
  
  // Handlers for Configuration
  const handleSaveConfig = useCallback((newConfig) => {
    setBackupConfig(newConfig);
    // Notification handled in child component
  }, []);

  // Custom Styles to enforce professional aesthetic (small font, specific faces)
  const pageStyle = {
    fontFamily: '"Roboto", "Poppins", sans-serif',
  };

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen" style={pageStyle}>
      {/* HEADER */}
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-xl font-bold text-gray-800 flex items-center">
          <Archive className="w-6 h-6 mr-3 text-green-600" />
          Data Backup & Restore
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage disaster recovery settings, schedule automated backups, and access historical system snapshots.
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column (Config & Manual) */}
        <div className="lg:col-span-1 space-y-6">
          <BackupConfiguration 
            config={backupConfig} 
            onSave={handleSaveConfig} 
            notify={notify} 
          />
          <ManualBackup
            notify={notify}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
          />
        </div>

        {/* Right Column (History) */}
        <div className="lg:col-span-2">
          <BackupHistory 
            history={backupHistory}
            notify={notify} 
          />
        </div>
      </div>

      {/* Custom Notification Box (Instead of alert()) */}
      <div 
        className={`fixed bottom-4 right-4 p-3 rounded-lg shadow-xl text-xs font-medium transition-all duration-300 
                    ${visible ? 'translate-y-0 opacity-100 bg-gray-800 text-white' : 'translate-y-4 opacity-0'}`}
      >
        {message}
      </div>
    </div>
  );
}